import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCheck, Sparkles, Zap, Lock, Bot } from "lucide-react";
import { useLead } from "../../context/LeadContext";
import { api } from "../../lib/api";

const SCRIPT = [
  {
    q: () => "Hi 👋 I'm Monk, Digimonk's AI sales agent. Quick one: what kind of business do you run?",
    key: "industry",
    options: ["Real Estate", "E-commerce", "Healthcare", "Education", "Other"],
  },
  {
    q: (a) => `${a.industry}, love it. 🚀 Roughly how many leads do you get every month?`,
    key: "volume",
    options: ["Under 100", "100–500", "500–2,000", "2,000+"],
  },
  {
    q: () => "Got it. What's slowing your team down the most right now?",
    key: "pain",
    options: ["Slow follow-ups", "Unqualified leads", "Missed enquiries", "Team bandwidth"],
  },
  {
    q: () => "Makes sense. When are you looking to fix this?",
    key: "urgency",
    options: ["ASAP 🔥", "This quarter", "Just exploring"],
  },
];

const score = (a) => {
  let s = 10;
  s += { "Under 100": 8, "100–500": 22, "500–2,000": 34, "2,000+": 45 }[a.volume] || 0;
  s += { "ASAP 🔥": 30, "This quarter": 20, "Just exploring": 8 }[a.urgency] || 0;
  s += a.pain === "Unqualified leads" ? 20 : 14;
  return Math.min(99, s);
};
const tagFor = (s) => (s >= 70 ? "Hot" : s >= 45 ? "Warm" : "Cold");

const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

let idc = 0;
const uid = () => `m${++idc}`;

export const WhatsAppSimulator = () => {
  const { openLeadForm } = useLead();
  const [mode, setMode] = useState("scripted");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [qualified, setQualified] = useState(null);
  const [input, setInput] = useState("");
  const [aiTag, setAiTag] = useState(null);
  const sessionId = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);
  const didInit = useRef(false);

  const pushBot = useCallback((text, delay = 900) => {
    setTyping(true);
    return new Promise((resolve) => {
      setTimeout(() => {
        setTyping(false);
        setMessages((m) => [...m, { id: uid(), role: "bot", text, time: nowTime() }]);
        resolve();
      }, delay);
    });
  }, []);

  const reset = useCallback(
    (nextMode) => {
      setMessages([]);
      setStepIdx(0);
      setAnswers({});
      setQualified(null);
      setAiTag(null);
      setInput("");
      setTyping(false);
      sessionId.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      if (nextMode === "scripted") {
        pushBot(SCRIPT[0].q({}), 700);
      } else {
        pushBot("Hey! I'm live now 🤖 Tell me about your business and I'll qualify you in real time. What do you do?", 700);
      }
    },
    [pushBot]
  );

  // init
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    reset("scripted");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    reset(m);
  };

  // ---- Scripted flow ----
  const chooseScripted = async (opt) => {
    const step = SCRIPT[stepIdx];
    setMessages((m) => [...m, { id: uid(), role: "user", text: opt, time: nowTime() }]);
    const nextAnswers = { ...answers, [step.key]: opt };
    setAnswers(nextAnswers);
    const next = stepIdx + 1;
    setStepIdx(next);

    if (next < SCRIPT.length) {
      await pushBot(SCRIPT[next].q(nextAnswers));
    } else {
      const s = score(nextAnswers);
      const t = tagFor(s);
      setQualified({ score: s, tag: t, answers: nextAnswers });
      await pushBot(
        t === "Hot"
          ? `You're a strong fit 🔥 With ${nextAnswers.volume} leads a month, our AI agent could recover a big slice you're losing to "${nextAnswers.pain.toLowerCase()}".`
          : t === "Warm"
          ? `Solid potential 👀 We can definitely tighten up "${nextAnswers.pain.toLowerCase()}" for you.`
          : "Thanks for sharing! Even at your stage, automating qualification pays off early."
      );
      await pushBot(`Your lead score: ${s}/100. You're a ${t} lead. Want to see your personalized demo?`, 1100);
    }
  };

  // ---- AI flow ----
  const sendAi = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || typing) return;
    setInput("");
    const history = messages.map((m) => ({ role: m.role === "bot" ? "assistant" : "user", text: m.text }));
    setMessages((m) => [...m, { id: uid(), role: "user", text, time: nowTime() }]);
    setTyping(true);
    try {
      const { data } = await api.post("/simulator/chat", {
        session_id: sessionId.current,
        message: text,
        history,
        industry: answers.industry || null,
      });
      setTyping(false);
      setMessages((m) => [...m, { id: uid(), role: "bot", text: data.reply, time: nowTime() }]);
      if (data.tag) setAiTag(data.tag);
    } catch {
      setTyping(false);
      setMessages((m) => [
        ...m,
        { id: uid(), role: "bot", text: "Hmm, my connection blipped. Mind trying that again?", time: nowTime() },
      ]);
    }
  };

  const unlock = () => {
    const q = qualified;
    openLeadForm({
      fields: q?.answers?.industry ? { industry: mapIndustry(q.answers.industry) } : {},
      leadScore: q?.score ?? null,
      tag: q?.tag ?? aiTag ?? null,
      qualification: q?.answers ?? { via: "ai" },
      source: mode === "ai" ? "simulator_ai" : "simulator_scripted",
    });
  };

  const showUnlock = Boolean(qualified) || Boolean(aiTag);

  return (
    <div className="relative w-full max-w-[400px]" data-testid="whatsapp-simulator">
      {/* floating badge */}
      <div className="absolute -left-4 -top-4 z-20 hidden rotate-[-6deg] rounded-xl border border-silver-300/70 bg-white px-3 py-1.5 text-xs font-bold text-charcoal shadow-lg sm:block">
        <span className="text-brand">●</span> Live simulator
      </div>

      <div className="overflow-hidden rounded-[2rem] border-[6px] border-charcoal bg-charcoal shadow-2xl">
        {/* header */}
        <div className="flex items-center gap-3 bg-whatsapp-header px-4 py-3">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
            <Bot className="h-5 w-5 text-white" />
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-whatsapp-header bg-brand" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold text-white">Monk · AI Sales Agent</p>
            <p className="text-[11px] text-white/70">{typing ? "typing…" : "online"}</p>
          </div>
          <div className="ml-auto flex gap-1 rounded-full bg-black/20 p-0.5">
            <ModeBtn active={mode === "scripted"} onClick={() => switchMode("scripted")} testid="mode-scripted">
              <Zap className="h-3 w-3" /> Scripted
            </ModeBtn>
            <ModeBtn active={mode === "ai"} onClick={() => switchMode("ai")} testid="mode-ai">
              <Sparkles className="h-3 w-3" /> AI
            </ModeBtn>
          </div>
        </div>

        {/* messages */}
        <div ref={scrollRef} className="wa-bg h-[380px] space-y-2 overflow-y-auto px-3.5 py-4" data-lenis-prevent>
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
          </AnimatePresence>
          {typing && <Typing />}

          {showUnlock && (
            <motion.button
              data-testid="simulator-unlock-btn"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={unlock}
              className="mx-auto mt-2 flex w-[92%] items-center justify-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-bold text-white shadow-lg transition hover:bg-brand-hover"
            >
              <Lock className="h-4 w-4" /> Unlock my personalized demo
            </motion.button>
          )}
        </div>

        {/* input area */}
        <div className="bg-[#F0F0F0] px-3 py-2.5">
          {mode === "scripted" ? (
            <div className="flex flex-wrap gap-2">
              <AnimatePresence>
                {!qualified && !typing && SCRIPT[stepIdx] &&
                  SCRIPT[stepIdx].options.map((opt) => (
                    <motion.button
                      key={opt}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      data-testid={`quick-reply-${opt}`}
                      onClick={() => chooseScripted(opt)}
                      className="rounded-full border border-brand/40 bg-white px-3.5 py-1.5 text-xs font-semibold text-charcoal shadow-sm transition hover:border-brand hover:bg-brand-light"
                    >
                      {opt}
                    </motion.button>
                  ))}
              </AnimatePresence>
              {(qualified || (!SCRIPT[stepIdx] && !typing)) && (
                <button
                  data-testid="simulator-restart-btn"
                  onClick={() => reset("scripted")}
                  className="rounded-full border border-silver-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-silver-700 hover:bg-silver-100"
                >
                  ↻ Restart chat
                </button>
              )}
            </div>
          ) : (
            <form onSubmit={sendAi} className="flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message…"
                className="h-11 flex-1 rounded-full border border-silver-300 bg-white px-4 text-sm outline-none focus:border-brand"
              />
              <button
                type="submit"
                data-testid="ai-send-btn"
                disabled={typing}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand text-white transition hover:bg-brand-hover disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const mapIndustry = (opt) =>
  ({
    "Real Estate": "Real Estate",
    "E-commerce": "E-commerce / D2C",
    Healthcare: "Healthcare & Clinics",
    Education: "Education & Coaching",
    Other: "Other",
  }[opt] || "");

const ModeBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
      active ? "bg-white text-whatsapp-header" : "text-white/80 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const Bubble = ({ m }) => {
  const isUser = m.role === "user";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 28 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`relative max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[13.5px] leading-snug shadow-sm ${
          isUser ? "rounded-br-sm bg-whatsapp-bubble text-charcoal" : "rounded-bl-sm bg-white text-charcoal"
        }`}
      >
        {m.text}
        <span className="ml-2 inline-flex items-center gap-0.5 align-bottom text-[10px] text-silver-500">
          {m.time}
          {isUser && <CheckCheck className="h-3 w-3 text-[#34B7F1]" />}
        </span>
      </div>
    </motion.div>
  );
};

const Typing = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
    <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-white px-3.5 py-3 shadow-sm">
      <span className="wa-dot h-2 w-2 rounded-full bg-silver-500" />
      <span className="wa-dot h-2 w-2 rounded-full bg-silver-500" />
      <span className="wa-dot h-2 w-2 rounded-full bg-silver-500" />
    </div>
  </motion.div>
);

export default WhatsAppSimulator;
